/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import LinearProgressDisplay from '@brands/Components/ProgressDisplay/ProgressDisplay';
import { visitOptions } from '@brands/Dashboard/MedicalHistory/DentalHistory/utils/questions';
import { DentalHistorySubmit } from '@brands/Dashboard/MedicalHistory/DentalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button, Checkbox } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Container from '../../../../../Components/Container/Container';
import TextField from '../../../../../Components/Inputs/MuiTextField/TextField';
import closeIcon from '../Icons/selectPatientClose.svg';
import logo from '../Icons/selectPatientLogo.svg';
import styles from './styles.module.scss';

const dentalHistoryValidationSchema = yup.object({
  user_id: yup.number().optional(),

  last_dental_visit: yup
    .string()
    .required('Last dental visit is required')
    .transform((value, originalValue) => (originalValue?.trim() === '' ? undefined : value))
    .test('valid-or-dontknow', 'Please enter a valid date or select "Not sure"', (value) => {
      if (!value) return false;
      if (value === 'dontKnow') return true;

      const date = new Date(value);
      return !isNaN(date.getTime());
    })
    .test('not-in-future', "You can't enter a future date", (value) => {
      if (!value || value === 'dontKnow') return true;

      const enteredDate = new Date(value);
      const now = new Date();
      return enteredDate <= now;
    }),

  who_did_you_see: yup
    .string()
    .required('Dentist seen is required')
    .transform((value, originalValue) => (originalValue?.trim() === '' ? undefined : value))
    .test('valid-or-dontknow', 'Please enter a dentist name or select "Not sure"', (value) => {
      if (!value) return false;
      if (value === 'dontKnow') return true;
      return value.trim().length > 0;
    })
    .max(100, 'Dentist name is too long'),

  visit_type: yup.string().optional(),
});

const ClinicalDataStepNine = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { state } = useLocation();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<DentalHistorySubmit>({
    last_dental_visit: '',
    who_did_you_see: '',
    visit_type: '',
  });
  const [savedLastDentalVisit, setSavedLastDentalVisit] = useState('');
  const [savedWhoDidYouSee, setSavedWhoDidYouSee] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    clearErrors,
    getValues,
    reset,
  } = useForm<DentalHistorySubmit>({
    mode: 'onChange', // already present
    resolver: yupResolver(dentalHistoryValidationSchema),
    defaultValues: initialValues,
  });

  const onSubmit = async (data: DentalHistorySubmit): Promise<void | null> => {
    setLoading(true);
    try {
      const currentUserId = selectedPatient?.value as number;

      const updatedData = {
        ...data,
        user_id: currentUserId,
      };

      if (!objectId) {
        await addClinicalData('dental_history', updatedData);
      } else {
        await updateClinicalData('dental_history', objectId, updatedData);
      }

      const snapshot = getValues();
      setInitialValues(snapshot);
      reset(snapshot);
      setSavedLastDentalVisit('');
      setSavedWhoDidYouSee('');
      setLoading(false);
      showSnackbar('Dental history saved successfully!', 'success');
      roleNavigate('/payment-information', {
        state: {
          caseType: values.find((item) => item.form_question_id === 20)?.value.caseType as string,
          isPatientForm: true,
          shouldSeeLastModal: state || '',
          patientId: selectedPatient?.value ? selectedPatient?.value : '',
        },
      });
    } catch (error) {
      displayErrorDetails(error);
    }
  };

  const onInvalid = (e: any): void => {
    const error = Object.values(e)[0] as any;
    if (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const backToDashboard = (): void => {
    roleNavigate('/dashboard');
  };

  useEffect(() => {
    const fetchDentalHistory = async (): Promise<void> => {
      if (!selectedPatient?.value) return;

      setLoading(true);
      try {
        const { dental_history } = await listClinicalData(selectedPatient.value);
        if (dental_history) {
          setObjectId(dental_history.id.toString());

          setValue('last_dental_visit', dental_history.last_dental_visit ?? '', { shouldValidate: true });
          setValue('who_did_you_see', dental_history.who_did_you_see ?? '', { shouldValidate: true });
          setValue('visit_type', dental_history.visit_type ?? '', { shouldValidate: true });

          const snapshot = {
            last_dental_visit: dental_history.last_dental_visit ?? '',
            who_did_you_see: dental_history.who_did_you_see ?? '',
            visit_type: dental_history.visit_type ?? '',
          };

          setInitialValues(snapshot);
          reset(snapshot);
        }
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    };
    fetchDentalHistory();
  }, [reset, selectedPatient?.value, setValue]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <div className={styles.clinicalDataIntroTopDiv}>
          <div className={styles.clinicalDataIntroTopDivFirst}>
            <img src={logo} className={styles.clinicalDataIntroTopDivFirstLogo} alt="Logo" />
            <div onClick={backToDashboard}>
              <img src={closeIcon} className={styles.clinicalDataIntroTopDivFirstClose} alt="Close" />
            </div>
          </div>
          <LinearProgressDisplay progress={10} />
        </div>
        <form className={`${styles.formContainer}`} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroStep}>
                <span>MEDICAL HISTORY: 9 OF 9</span>
              </div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>When is the last time you saw a dentist in person?</span>
                <span className={styles.clinicalDataIntroTitleRequired}> *</span>
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                <DateTextField
                  {...register('last_dental_visit')}
                  label="Last dental appointment"
                  value={
                    watch('last_dental_visit') && watch('last_dental_visit') !== 'dontKnow'
                      ? dayjs(watch('last_dental_visit'))
                      : null
                  }
                  errors={errors.last_dental_visit ? errors : {}}
                  name="date_of_birth"
                  onChange={(newValue: Dayjs | null) => {
                    if (errors.last_dental_visit) clearErrors('last_dental_visit');
                    const formattedDate = newValue ? dayjs(newValue).format('MM/DD/YYYY') : '';
                    setValue('last_dental_visit', formattedDate, { shouldValidate: true });
                  }}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.inputWrap}`}
                />
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('last_dental_visit') === 'dontKnow'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        setSavedLastDentalVisit(watch('last_dental_visit') || '');
                        setValue('last_dental_visit', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('last_dental_visit', savedLastDentalVisit || '', { shouldValidate: true });
                      }
                      if (errors.last_dental_visit) clearErrors('last_dental_visit');
                    }}
                    sx={{ color: 'rgba(23, 36, 51, 0.87)', '&.Mui-checked': { color: '#00A1E0' } }}
                    name="not_sure"
                    value="not_sure"
                  />
                  <label className={styles.checkboxLabel} htmlFor="not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent}>
                <TextField
                  {...register('who_did_you_see')}
                  value={watch('who_did_you_see') === 'dontKnow' ? '' : watch('who_did_you_see')}
                  label="Dentist Seen"
                  errors={errors.who_did_you_see || {}}
                  onChange={(val) => setValue('who_did_you_see', val.target.value, { shouldValidate: true })}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.inputWrap}`}
                />
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('who_did_you_see') === 'dontKnow'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        setSavedWhoDidYouSee(watch('who_did_you_see') || '');
                        setValue('who_did_you_see', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('who_did_you_see', savedWhoDidYouSee || '', { shouldValidate: true });
                      }
                      if (errors.who_did_you_see) clearErrors('who_did_you_see');
                    }}
                    sx={{ color: 'rgba(23, 36, 51, 0.87)', '&.Mui-checked': { color: '#00A1E0' } }}
                    name="not_sure"
                    value="not_sure"
                    data-testid="not-sure-checkbox"
                  />
                  <label className={styles.checkboxLabel} htmlFor="not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent}>
                <MUISelectInput
                  {...register('visit_type')}
                  data-testid="complete-profile-visit-type"
                  label="Reason for visit (optional)"
                  errors={errors.visit_type ? errors : {}}
                  name="visit_type"
                  onChange={(selectedValue: string) => {
                    if (errors.visit_type) clearErrors('visit_type');
                    setValue('visit_type', selectedValue, { shouldValidate: true });
                  }}
                  value={watch('visit_type')}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.inputWrap}`}
                  options={visitOptions}
                />
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={loading || !isValid}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepNine;
